import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAppOption } from '~/types/option'

import { createPlacement } from '~/api/placements'
import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FORM_ERRORS } from '~/const'
import { useAppsListQuery } from '~/queries/applications'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'create-placement-modal'

interface FormValues {
  appKey: IOption | null
  key: string
}

const defaultValues: FormValues = {
  appKey: null,
  key: '',
}

const validationSchema = Yup.object({
  appKey: RequiredYupOption.nullable().notOneOf([null], FORM_ERRORS.REQUIRED),
  key: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const CreatePlacementForm = ({ onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  const { data: apps, isLoading: isAppsLoading } = useAppsListQuery({ owned: true })

  const { mutateAsync: createPlacementMutation } = useMutation({ mutationFn: createPlacement })

  const appKeyOptions: IAppOption[] =
    apps?.data?.map(({ app_icon_url, app_key, device_type }) => ({
      appKey: app_key,
      deviceType: device_type,
      label: `[${device_type}] ${app_key}`,
      url: app_icon_url,
      value: app_key,
    })) || []

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Handlers */

  const handleFormSubmit = async ({ appKey, key }: FormValues) => {
    try {
      await createPlacementMutation({ appKey: (appKey as IOption).value, key })
      onSuccess()
    } catch (e) {
      captureException(e)
      onFailure()
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <Controller
            control={control}
            name="appKey"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                components={{ Option: CustomAppOption }}
                error={errors.appKey?.message}
                isLoading={isAppsLoading}
                label="App key"
                options={appKeyOptions}
                placeholder="Select the app key"
                required
              />
            )}
          />
          <TextInput {...register('key')} error={errors.key?.message} label="Key" placeholder="Enter a key" required />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const CreatePlacementModal = ({ isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal className="create-placement-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Create a placement</ModalHeader>
      <CreatePlacementForm onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default CreatePlacementModal
